/** Ler e escrever folhas de cálculo (.xlsx e .csv), sem bibliotecas.
 *
 *  Um .xlsx é um zip com XML dentro (Office Open XML, ECMA-376 parte 1,
 *  SpreadsheetML). Para o que precisamos, uma tabela de texto e números
 *  por folha, bastam meia dúzia de ficheiros e o `fflate` que já vem
 *  para os .ifczip e o KMZ. Trazer uma biblioteca de Excel inteira para
 *  isto era pagar em tamanho e em actualizações por coisas que não
 *  usamos (fórmulas, estilos, gráficos).
 *
 *  O que fica de fora, de propósito: fórmulas (lê-se o valor em cache
 *  que o Excel lá deixa), datas (vêm como o número de série do Excel,
 *  tal como estão no ficheiro), células fundidas e estilos. */

import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate'

export type Celula = string | number | boolean | null

export interface Folha {
  nome: string
  linhas: Celula[][]
}

export interface FolhaLida {
  nome: string
  /** Tudo em texto, como se vê na célula: números com ponto decimal,
   *  booleanos como TRUE/FALSE, células vazias como ''. */
  linhas: string[][]
}

// ---------------------------------------------------------------------
// Escrever
// ---------------------------------------------------------------------

/** Caracteres de controlo não são XML válido: o Excel recusa o ficheiro
 *  inteiro por causa de um só. Ficam a tabulação e as quebras de linha. */
function semControlo(s: string): string {
  let out = ''
  for (const ch of s) {
    const c = ch.charCodeAt(0)
    if (c >= 32 || c === 9 || c === 10 || c === 13) out += ch
  }
  return out
}

function xmlEsc(s: string): string {
  return semControlo(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Coluna 0 -> A, 25 -> Z, 26 -> AA. */
export function letraDaColuna(i: number): string {
  let s = ''
  let n = i + 1
  while (n > 0) {
    const r = (n - 1) % 26
    s = String.fromCharCode(65 + r) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

/** Nomes de folha: até 31 caracteres, sem : \ / ? * [ ]. */
function nomeDeFolha(nome: string, usados: Set<string>): string {
  const base = nome.replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'Folha'
  let n = 2
  let final = base
  while (usados.has(final.toLowerCase())) {
    const suf = ` (${n++})`
    final = base.slice(0, 31 - suf.length) + suf
  }
  usados.add(final.toLowerCase())
  return final
}

function folhaXml(f: Folha): string {
  const largura = f.linhas.reduce((m, l) => Math.max(m, l.length), 0)
  const partes: string[] = []
  partes.push(
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    // A primeira linha fica presa e com filtro: é o cabeçalho.
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>',
  )
  if (largura > 0) {
    partes.push('<cols>')
    for (let c = 0; c < largura; c++) {
      let max = 8
      for (const l of f.linhas.slice(0, 200)) {
        const v = l[c]
        if (v !== null && v !== undefined) max = Math.max(max, String(v).length)
      }
      partes.push(`<col min="${c + 1}" max="${c + 1}" width="${Math.min(60, max + 2)}" customWidth="1"/>`)
    }
    partes.push('</cols>')
  }
  partes.push('<sheetData>')
  f.linhas.forEach((linha, r) => {
    partes.push(`<row r="${r + 1}">`)
    linha.forEach((v, c) => {
      if (v === null || v === undefined || v === '') return
      const ref = `${letraDaColuna(c)}${r + 1}`
      const estilo = r === 0 ? ' s="1"' : ''
      if (typeof v === 'number' && Number.isFinite(v)) {
        partes.push(`<c r="${ref}"${estilo}><v>${v}</v></c>`)
      } else if (typeof v === 'boolean') {
        partes.push(`<c r="${ref}" t="b"${estilo}><v>${v ? 1 : 0}</v></c>`)
      } else {
        partes.push(
          `<c r="${ref}" t="inlineStr"${estilo}><is><t xml:space="preserve">${xmlEsc(String(v))}</t></is></c>`,
        )
      }
    })
    partes.push('</row>')
  })
  partes.push('</sheetData>')
  if (largura > 0 && f.linhas.length > 0) {
    partes.push(`<autoFilter ref="A1:${letraDaColuna(largura - 1)}${f.linhas.length}"/>`)
  }
  partes.push('</worksheet>')
  return partes.join('')
}

/** Escreve um livro com uma ou mais folhas. A primeira linha de cada
 *  folha sai a negrito, presa e com filtro. */
export function escreverXlsx(folhas: Folha[]): Uint8Array<ArrayBuffer> {
  if (folhas.length === 0) throw new Error('Um livro precisa de pelo menos uma folha.')
  const usados = new Set<string>()
  const nomes = folhas.map((f) => nomeDeFolha(f.nome, usados))
  const ficheiros: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        folhas
          .map(
            (_, i) =>
              `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
          )
          .join('') +
        '</Types>',
    ),
    '_rels/.rels': strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    ),
    'xl/workbook.xml': strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>' +
        nomes.map((n, i) => `<sheet name="${xmlEsc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
        '</sheets></workbook>',
    ),
    'xl/_rels/workbook.xml.rels': strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        folhas
          .map(
            (_, i) =>
              `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
          )
          .join('') +
        `<Relationship Id="rId${folhas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
        '</Relationships>',
    ),
    'xl/styles.xml': strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
        '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
        '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
        '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
        '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>' +
        '</styleSheet>',
    ),
  }
  folhas.forEach((f, i) => {
    ficheiros[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(folhaXml(f))
  })
  // Cópia com ArrayBuffer próprio: o Blob não aceita uma vista sobre
  // um buffer que possa ser partilhado.
  return new Uint8Array(zipSync(ficheiros, { level: 6 }))
}

// ---------------------------------------------------------------------
// Ler
// ---------------------------------------------------------------------

function xmlDesc(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(parseInt(d, 10)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
    // O Excel escreve caracteres que o XML não aceita como _xHHHH_.
    .replace(/_x([0-9A-F]{4})_/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)))
}

/** Junta o texto de todos os <t> de um bocado de XML (texto rico
 *  vem partido em vários <r><t>). */
function textoDosT(xml: string): string {
  let out = ''
  const re = /<t(?:\s[^>]*)?>([\s\S]*?)<\/t>|<t(?:\s[^>]*)?\/>/g
  let m: RegExpExecArray | null
  while ((m = re.exec(xml))) out += m[1] ? xmlDesc(m[1]) : ''
  return out
}

/** "BC12" -> índice de coluna 54. */
function colunaDaRef(ref: string): number {
  let n = 0
  for (const ch of ref) {
    const c = ch.charCodeAt(0)
    if (c < 65 || c > 90) break
    n = n * 26 + (c - 64)
  }
  return n - 1
}

function atributo(tag: string, nome: string): string | null {
  const m = new RegExp(`\\s${nome}="([^"]*)"`).exec(tag)
  return m ? xmlDesc(m[1]) : null
}

function normalizarCaminho(base: string, alvo: string): string {
  if (alvo.startsWith('/')) return alvo.slice(1)
  const partes = base.split('/').slice(0, -1)
  for (const p of alvo.split('/')) {
    if (p === '..') partes.pop()
    else if (p !== '.') partes.push(p)
  }
  return partes.join('/')
}

/** O número de uma célula como o Excel o mostra. O Excel grava o double
 *  com 17 algarismos (2,3 fica "2.2999999999999998"), mas só garante 15:
 *  arredonda-se aos 15 e escreve-se na forma mais curta. Um valor que
 *  não seja número passa tal e qual. */
export function numeroDaCelula(v: string): string {
  const t = v.trim()
  if (!/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(t)) return v
  const n = Number(t)
  if (!Number.isFinite(n)) return v
  return String(Number(n.toPrecision(15)))
}

export function lerXlsx(bytes: Uint8Array): FolhaLida[] {
  let zip: Record<string, Uint8Array>
  try {
    zip = unzipSync(bytes)
  } catch {
    throw new Error('O ficheiro não é um .xlsx válido (não abre como zip).')
  }
  const ler = (p: string) => (zip[p] ? strFromU8(zip[p]) : null)
  const livro = ler('xl/workbook.xml')
  if (!livro) throw new Error('O ficheiro não tem livro (xl/workbook.xml). É mesmo um .xlsx?')

  const rels = new Map<string, string>()
  const relsXml = ler('xl/_rels/workbook.xml.rels') ?? ''
  for (const m of relsXml.matchAll(/<Relationship\b[^>]*>/g)) {
    const id = atributo(m[0], 'Id')
    const alvo = atributo(m[0], 'Target')
    if (id && alvo) rels.set(id, normalizarCaminho('xl/workbook.xml', alvo))
  }

  const partilhadas: string[] = []
  const sst = ler('xl/sharedStrings.xml')
  if (sst) {
    for (const m of sst.matchAll(/<si>([\s\S]*?)<\/si>|<si\/>/g)) partilhadas.push(m[1] ? textoDosT(m[1]) : '')
  }

  const out: FolhaLida[] = []
  for (const m of livro.matchAll(/<sheet\b[^>]*>/g)) {
    const nome = atributo(m[0], 'name') ?? `Folha${out.length + 1}`
    const rid = atributo(m[0], 'r:id')
    const caminho = rid ? rels.get(rid) : undefined
    const xml = caminho ? ler(caminho) : null
    if (!xml) continue
    const linhas: string[][] = []
    // A forma auto-fechada tem de se tentar primeiro: o Excel escreve
    // assim as linhas vazias com formatação (<row r="2" ht="30" .../>),
    // e a forma com corpo apanhava-a e engolia a linha seguinte.
    for (const row of xml.matchAll(/<row\b([^>]*?)\/>|<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
      const attrsLinha = row[1] ?? row[2] ?? ''
      const rAttr = atributo(`<row ${attrsLinha}>`, 'r')
      const r = rAttr ? parseInt(rAttr, 10) - 1 : linhas.length
      const linha: string[] = []
      let proxima = 0
      for (const c of (row[3] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const tag = `<c ${c[1]}>`
        const ref = atributo(tag, 'r')
        const col = ref ? colunaDaRef(ref) : proxima
        proxima = col + 1
        const t = atributo(tag, 't')
        const corpo = c[2] ?? ''
        const v = /<v>([\s\S]*?)<\/v>/.exec(corpo)?.[1]
        let texto: string
        if (t === 's') texto = v !== undefined ? partilhadas[parseInt(v, 10)] ?? '' : ''
        else if (t === 'inlineStr') texto = textoDosT(corpo)
        else if (t === 'b') texto = v === '1' ? 'TRUE' : 'FALSE'
        else if (t === 'str' || t === 'e') texto = v !== undefined ? xmlDesc(v) : ''
        else texto = v !== undefined ? numeroDaCelula(xmlDesc(v)) : ''
        while (linha.length < col) linha.push('')
        linha[col] = texto
      }
      while (linhas.length < r) linhas.push([])
      linhas[r] = linha
    }
    out.push({ nome, linhas })
  }
  return out
}

// ---------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------

/** O separador mais provável da primeira linha: ; (Excel em português),
 *  vírgula ou tabulação, contando só fora de aspas. */
function separadorProvavel(texto: string): string {
  const primeira = texto.split(/\r?\n/, 1)[0] ?? ''
  const contagem: Record<string, number> = { ';': 0, ',': 0, '\t': 0 }
  let dentro = false
  for (const ch of primeira) {
    if (ch === '"') dentro = !dentro
    else if (!dentro && ch in contagem) contagem[ch]++
  }
  const [melhor, vezes] = Object.entries(contagem).sort((a, b) => b[1] - a[1])[0]
  return vezes > 0 ? melhor : ';'
}

/** CSV (RFC 4180), com aspas e quebras de linha dentro de campos. */
export function lerCsv(texto: string, separador?: string): string[][] {
  const semBom = texto.charCodeAt(0) === 0xfeff ? texto.slice(1) : texto
  // "sep=;" na primeira linha é uma indicação para o Excel, não dados.
  const indicacao = /^sep=(.)\r?\n/i.exec(semBom)
  const t = indicacao ? semBom.slice(indicacao[0].length) : semBom
  const sep = separador ?? indicacao?.[1] ?? separadorProvavel(t)
  const linhas: string[][] = []
  let linha: string[] = []
  let campo = ''
  let dentro = false
  for (let i = 0; i < t.length; i++) {
    const ch = t[i]
    if (dentro) {
      if (ch === '"') {
        if (t[i + 1] === '"') {
          campo += '"'
          i++
        } else dentro = false
      } else campo += ch
      continue
    }
    if (ch === '"') dentro = true
    else if (ch === sep) {
      linha.push(campo)
      campo = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && t[i + 1] === '\n') i++
      linha.push(campo)
      linhas.push(linha)
      linha = []
      campo = ''
    } else campo += ch
  }
  if (campo !== '' || linha.length > 0) {
    linha.push(campo)
    linhas.push(linha)
  }
  return linhas
}

/** Um CSV para o Excel: ";" a separar, vírgula decimal, a linha "sep=;"
 *  no início (para o Excel de qualquer língua separar as colunas) e em
 *  UTF-16 com BOM. Em UTF-8 o Excel ignora o BOM quando há "sep=" e
 *  estraga os acentos (medido no Excel: "Área" ficava "Ãrea"). */
export function escreverCsvParaExcel(linhas: Celula[][]): Uint8Array<ArrayBuffer> {
  const esc = (v: Celula): string => {
    if (v === null || v === undefined) return ''
    if (typeof v === 'number') return Number.isFinite(v) ? String(v).replace('.', ',') : ''
    if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
    return /[";\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
  }
  const texto = ['sep=;', ...linhas.map((l) => l.map(esc).join(';'))].join('\r\n') + '\r\n'
  const out = new Uint8Array(2 + texto.length * 2)
  out[0] = 0xff
  out[1] = 0xfe
  for (let i = 0; i < texto.length; i++) {
    const c = texto.charCodeAt(i)
    out[2 + i * 2] = c & 0xff
    out[3 + i * 2] = c >> 8
  }
  return out
}

/** Lê .xlsx ou .csv pelo conteúdo (um zip começa por PK). */
export function lerFolhas(bytes: Uint8Array, nomeDoFicheiro = ''): FolhaLida[] {
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return lerXlsx(bytes)
  const utf16 = bytes[0] === 0xff && bytes[1] === 0xfe
  const texto = new TextDecoder(utf16 ? 'utf-16le' : 'utf-8').decode(bytes)
  return [{ nome: nomeDoFicheiro.replace(/\.[^.]+$/, '') || 'CSV', linhas: lerCsv(texto) }]
}
